/**
 * <routine-quiz> — routine finder (templates/page.quiz.json, sections/quiz-finder.liquid).
 *
 * Markup contract (rendered by the section):
 *   [data-quiz-step=i]            one panel per question; contains [data-quiz-option] radios (data-key, data-label, data-why, data-max)
 *   <template data-quiz-result-def data-q1..data-q4 data-name data-why-1 data-why-2>  <li.quiz-row data-variant-id data-price data-step data-step-label data-title>…
 *   The first result template whose rules match the answers wins. Chosen options' data-max (smallest > 0) trims the product list.
 *   A result with no products uses <template data-quiz-fallback> (fallback collection rows); with no rows at all it shows [data-quiz-empty] (shop all + WhatsApp).
 * Add to cart uses RM.addToCart (product-form.js) and opens the cart drawer. WhatsApp: wa.me/<number>?text=…
 */
(() => {
  const RM = (window.RM = window.RM || {});

  const pad2 = (n) => String(n).padStart(2, '0');

  const withDelimiters = (number, precision, thousands, decimal) => {
    const fixed = (number / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? grouped + decimal + fraction : grouped;
  };

  /** Mirrors Shopify's money_without_trailing_zeros for the store's money_format. */
  const formatMoney = (cents, format) => {
    const match = (format || '{{amount}}').match(/\{\{\s*(\w+)\s*\}\}/);
    const type = match ? match[1] : 'amount';
    let value;
    switch (type) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = cents % 100 === 0 ? withDelimiters(cents, 0, '.', ',') : withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = cents % 100 === 0 ? withDelimiters(cents, 0, "'", '.') : withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = cents % 100 === 0 ? withDelimiters(cents, 0, ' ', ',') : withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = cents % 100 === 0 ? withDelimiters(cents, 0, ',', '.') : withDelimiters(cents, 2, ',', '.');
    }
    return format ? format.replace(/\{\{\s*\w+\s*\}\}/, value) : value;
  };

  class RoutineQuiz extends HTMLElement {
    connectedCallback() {
      this.total = Number(this.dataset.total) || 0;
      this.steps = [...this.querySelectorAll('[data-quiz-step]')];
      this.answers = new Array(this.total).fill(null);
      this.step = 0;
      this.timer = null;
      this.rows = [];
      this.questionsEl = this.querySelector('[data-quiz-questions]');
      this.resultEl = this.querySelector('[data-quiz-result]');
      this.nextBtn = this.querySelector('[data-quiz-next]');
      this.backBtn = this.querySelector('[data-quiz-back]');
      if (this.steps.length === 0) return;

      this.addEventListener('click', (event) => this.#onClick(event));
      this.addEventListener('keydown', (event) => this.#onKeydown(event));
      this.#showStep(0);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    #q(selector) {
      return this.querySelector(selector);
    }

    #onClick(event) {
      const option = event.target.closest('[data-quiz-option]');
      if (option) return this.#choose(option);
      if (event.target.closest('[data-quiz-next]')) return this.#next();
      if (event.target.closest('[data-quiz-back]')) return this.#back();
      if (event.target.closest('[data-quiz-restart]')) return this.#restart();
      if (event.target.closest('[data-quiz-add]')) return this.#addToCart(event.target.closest('[data-quiz-add]'));
      if (event.target.closest('[data-quiz-whatsapp]')) return this.#sendWhatsApp();
    }

    #onKeydown(event) {
      const group = event.target.closest('.quiz__options');
      if (!group || !['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
      const radios = [...group.querySelectorAll('[data-quiz-option]')];
      const current = radios.indexOf(event.target.closest('[data-quiz-option]'));
      if (current < 0) return;
      event.preventDefault();
      const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
      const next = radios[(current + delta + radios.length) % radios.length];
      next.focus();
      this.#choose(next, { advance: false });
    }

    /* ---------- questions ---------- */
    #stepOptions(index) {
      return [...this.steps[index].querySelectorAll('[data-quiz-option]')];
    }

    #choose(option, { advance = true } = {}) {
      const index = this.step;
      this.answers[index] = option.dataset.key;
      this.#stepOptions(index).forEach((el) => {
        const on = el === option;
        el.setAttribute('aria-checked', String(on));
        el.tabIndex = on ? 0 : -1;
      });
      this.nextBtn.disabled = false;
      clearTimeout(this.timer);
      if (advance) {
        this.timer = setTimeout(() => {
          if (this.step === index) this.#next();
        }, 380);
      }
    }

    #showStep(index, { focus = false } = {}) {
      this.step = index;
      this.steps.forEach((panel, i) => {
        panel.hidden = i !== index;
        if (i === index) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        }
      });
      this.#q('[data-quiz-num]').textContent = String(index + 1);
      const progress = this.#q('[data-quiz-progress]');
      progress.setAttribute('aria-valuenow', String(index + 1));
      progress.querySelectorAll('.quiz__seg').forEach((seg, i) => seg.classList.toggle('is-on', i <= index));
      this.#q('[data-quiz-count]').textContent = this.dataset.msgQuestion
        .replace('__N__', String(index + 1))
        .replace('__T__', String(this.total));
      this.backBtn.disabled = index === 0;
      this.nextBtn.disabled = this.answers[index] === null;
      this.#q('[data-quiz-next-label]').textContent = index === this.total - 1 ? this.dataset.msgSee : this.dataset.msgNext;
      const checked = this.#stepOptions(index).find((el) => el.dataset.key === this.answers[index]);
      this.#stepOptions(index).forEach((el, i) => {
        const on = checked ? el === checked : i === 0;
        el.setAttribute('aria-checked', String(el === checked));
        el.tabIndex = on ? 0 : -1;
      });
      if (focus) this.steps[index].querySelector('.quiz__h1')?.focus({ preventScroll: true });
    }

    #next() {
      if (this.answers[this.step] === null) return;
      clearTimeout(this.timer);
      if (this.step < this.total - 1) this.#showStep(this.step + 1, { focus: true });
      else this.#showResult();
    }

    #back() {
      clearTimeout(this.timer);
      if (this.step > 0) this.#showStep(this.step - 1, { focus: true });
    }

    /* ---------- result ---------- */
    #pickResult() {
      const defs = [...this.querySelectorAll('template[data-quiz-result-def]')];
      const match = defs.find((def) =>
        this.answers.every((answer, i) => {
          const rule = (def.dataset[`q${i + 1}`] || '').trim();
          if (!rule) return true;
          return rule.split(',').map((part) => part.trim()).includes(answer);
        })
      );
      return match || defs[defs.length - 1];
    }

    #showResult() {
      const def = this.#pickResult();
      if (!def) return;
      const chosen = this.answers.map((key, i) => this.#stepOptions(i).find((el) => el.dataset.key === key));

      const toRows = (root) => [...root.querySelectorAll('li.quiz-row')].map((li) => document.importNode(li, true));
      const bySteps = (list) =>
        list
          .map((row, index) => ({ row, index, step: Number(row.dataset.step) || 99 }))
          .sort((a, b) => a.step - b.step || a.index - b.index)
          .map((entry) => entry.row);
      const limits = chosen.map((el) => Number(el?.dataset.max) || 0).filter((n) => n > 0);
      const limit = limits.length ? Math.min(...limits) : Infinity;

      let rows = toRows(def.content);
      if (rows.length) {
        rows = bySteps(rows.slice(0, limit));
      } else {
        // No products assigned to this result: fall back to the fallback collection (sorted by step number).
        const fallback = this.querySelector('template[data-quiz-fallback]');
        rows = fallback ? bySteps(toRows(fallback.content)).slice(0, Math.min(4, limit)) : [];
      }
      this.rows = rows;

      rows.forEach((row) => {
        const role = row.querySelector('[data-role]');
        const step = Number(row.dataset.step);
        const label = (row.dataset.stepLabel || '').toUpperCase();
        if (role) {
          if (step && step !== 99 && label) {
            role.textContent = this.dataset.msgStep.replace('__N__', pad2(step)).replace('__L__', label);
          } else {
            role.remove();
          }
        }
      });
      this.#q('[data-quiz-rows]').replaceChildren(...rows);

      this.#q('[data-quiz-name]').textContent = def.dataset.name || '';
      const reasons = [...chosen.map((el) => el?.dataset.why), def.dataset.why1, def.dataset.why2].filter(Boolean);
      this.#fillList('[data-quiz-why]', reasons, 'quiz__why-item');
      this.#fillList('[data-quiz-recap]', chosen.map((el) => el?.dataset.label).filter(Boolean), 'rm-badge quiz__recap-item');

      const count = rows.length;
      const countLabel = this.#q('[data-quiz-count-label]');
      countLabel.textContent =
        count === 1 ? this.dataset.msgCountOne : this.dataset.msgCountOther.replace('__N__', String(count));
      countLabel.hidden = count === 0;
      this.#q('[data-quiz-empty]').hidden = count > 0;
      this.#q('[data-quiz-set]').hidden = count === 0;
      this.#q('[data-quiz-sticky]').hidden = count === 0;

      const sum = rows.reduce((acc, row) => acc + Number(row.dataset.price || 0), 0);
      const tierKey = Math.min(Math.max(count, 2), 4);
      const pct = count >= 2 ? Number(this.dataset[`tier${tierKey}`]) || 0 : 0;
      const badge = this.#q('[data-quiz-save]');
      badge.hidden = pct <= 0;
      badge.textContent = this.dataset.msgSave.replace('__PCT__', String(pct));
      const totalText = formatMoney(sum, this.dataset.moneyFormat);
      this.#q('[data-quiz-total]').textContent = totalText;
      this.#q('[data-quiz-total-mirror]').textContent = totalText;
      this.querySelectorAll('[data-quiz-add]').forEach((btn) => {
        btn.hidden = count === 0;
      });
      this.#setError('');
      this.#setStatus('');

      this.questionsEl.hidden = true;
      this.resultEl.hidden = false;
      this.resultEl.classList.remove('is-entering');
      void this.resultEl.offsetWidth;
      this.resultEl.classList.add('is-entering');
      window.scrollTo({ top: 0 });
      this.#q('[data-quiz-name]').focus({ preventScroll: true });
      this.resultName = def.dataset.name || '';
      this.resultTotal = totalText;
    }

    #fillList(selector, items, className) {
      const list = this.#q(selector);
      list.replaceChildren(
        ...items.map((text) => {
          const li = document.createElement('li');
          li.className = className;
          li.textContent = text;
          return li;
        })
      );
    }

    #restart() {
      clearTimeout(this.timer);
      this.answers.fill(null);
      this.steps.forEach((panel) => panel.querySelectorAll('[data-quiz-option]').forEach((el) => el.setAttribute('aria-checked', 'false')));
      this.resultEl.hidden = true;
      this.questionsEl.hidden = false;
      this.#showStep(0, { focus: true });
      window.scrollTo({ top: 0 });
    }

    #setError(message) {
      const box = this.#q('[data-quiz-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    }

    #setStatus(message) {
      const box = this.#q('[data-quiz-status]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    }

    async #addToCart(button) {
      if (!this.rows.length || button.getAttribute('aria-busy') === 'true') return;
      const buttons = [...this.querySelectorAll('[data-quiz-add]')];
      buttons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));
      this.#setError('');
      try {
        const items = this.rows.map((row) => ({ id: Number(row.dataset.variantId), quantity: 1 }));
        await RM.addToCart(items, { source: 'routine-quiz' });
      } catch (error) {
        this.#setError(error.message || this.dataset.msgAddError);
      } finally {
        buttons.forEach((btn) => btn.removeAttribute('aria-busy'));
      }
    }

    #sendWhatsApp() {
      const number = this.dataset.waNumber;
      if (!number) return;
      const contact = (this.#q('[data-quiz-contact]')?.value || '').trim();
      const products = this.rows.map((row) => row.dataset.title).join(', ');
      const lines = [`${this.dataset.waIntro} ${this.resultName}`, products, this.resultTotal];
      if (contact) lines.push(contact);
      const url = `https://wa.me/${number}?text=${encodeURIComponent(lines.filter(Boolean).join('\n'))}`;
      window.open(url, '_blank', 'noopener');
      this.#setStatus(this.dataset.msgWaOpened);
    }
  }

  if (!customElements.get('routine-quiz')) customElements.define('routine-quiz', RoutineQuiz);
})();
